import { useEffect } from 'react'
import { isNativeApp } from '../utils/platform'
import { useVoiceCallSession } from './use-voice-call-session'
import { beginIncomingCallAlert, endIncomingCallAlert } from '../utils/web-call-notification'
import { registerForWebPush } from '../services/web-push/web-push-registration'

/**
 * WEB-only background-tab alerting for incoming calls. Mirrors the in-app
 * ringtone hook but adds a system notification + title flash + background
 * ringtone when the tab is hidden/unfocused, so a user on another tab still
 * gets notified. Native builds rely on FCM push and are skipped.
 */
export function useWebCallAlert(currentUserId: string | undefined): void {
  useEffect(() => {
    if (isNativeApp() || !currentUserId) return

    // Notification permission is asked by setup ("Get alerts"), never primed here.
    // Subscribe to closed-tab Web Push if notification permission is already
    // granted (returning users / those who allowed during onboarding). New
    // grants trigger this from the permissions service. No-op otherwise.
    void registerForWebPush()

    // The service worker pings us to re-subscribe when the browser rotates the
    // push subscription (`pushsubscriptionchange`).
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'WEB_PUSH_RESUBSCRIBE') {
        void registerForWebPush({ force: true })
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [currentUserId])

  const session = useVoiceCallSession()
  const phase = session?.phase
  const callId = session?.callId

  useEffect(() => {
    if (isNativeApp()) return
    if (phase !== 'incoming' || !session) {
      endIncomingCallAlert()
      return
    }
    beginIncomingCallAlert({
      callId: session.callId,
      callerName: session.peer.displayName,
      mediaType: session.mediaType ?? 'voice',
    })
    return () => endIncomingCallAlert(session.callId)
    // Only re-run when the call identity/phase changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, callId])
}
